import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import { UserEligibilityDTO } from 'us-web-services';

interface UserLookupCardProps {
  title: string;
  fieldLabel: string;
  userId: string;
  onUserIdChange: (value: string) => void;
  onValidate: () => void;
  validating: boolean;
  validation: UserEligibilityDTO | null;
  applyingFix: string | null;
  onApplyAutoFix: (key: string, userType: 'PARENT' | 'SITTER') => void;
}

const UserLookupCard: React.FC<UserLookupCardProps> = ({
  title,
  fieldLabel,
  userId,
  onUserIdChange,
  onValidate,
  validating,
  validation,
  applyingFix,
  onApplyAutoFix,
}) => (
  <Card>
    <CardContent>
      <Typography variant='h6' gutterBottom>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label={fieldLabel}
          value={userId}
          onChange={(e) => onUserIdChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onValidate()}
          size='small'
          fullWidth
        />
        <IconButton onClick={onValidate} disabled={!userId || validating} color='primary'>
          {validating ? <CircularProgress size={24} /> : <SearchIcon />}
        </IconButton>
      </Box>
      {validation && (
        <Box sx={{ mt: 1 }}>
          <Typography variant='subtitle2' gutterBottom>
            {validation.displayName} ({validation.email})
          </Typography>
          {validation.isB2BUser && (
            <Chip
              label={`B2B: ${validation.companyName} - $${validation.careCreditBalance?.toFixed(2) || '0.00'} credit`}
              color='info'
              size='small'
              sx={{ mb: 1 }}
            />
          )}
          <Stack spacing={0.5}>
            {validation.checks.map((check) => (
              <Box key={check.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {check.passed ? (
                  <CheckCircleIcon color='success' fontSize='small' />
                ) : check.isWarning ? (
                  <WarningIcon color='warning' fontSize='small' />
                ) : (
                  <CancelIcon color='error' fontSize='small' />
                )}
                <Typography
                  variant='body2'
                  color={check.passed ? 'text.secondary' : check.isWarning ? 'warning.main' : 'error'}
                >
                  {check.message}
                </Typography>
                {check.canAutoFix && check.autoFixKey && (
                  <Chip
                    icon={
                      applyingFix === check.autoFixKey ? (
                        <CircularProgress size={12} color='inherit' />
                      ) : (
                        <AutoFixIcon />
                      )
                    }
                    label='Auto-fix'
                    size='small'
                    color='warning'
                    variant='outlined'
                    clickable
                    disabled={applyingFix !== null}
                    onClick={() => onApplyAutoFix(check.autoFixKey!, validation.userType)}
                  />
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </CardContent>
  </Card>
);

export default UserLookupCard;
